import { memo, ReactNode, useMemo, useState, useEffect } from "react";
import { useQueryClient } from "react-query";
import { Sidebar, Menu, MenuItem, sidebarClasses } from "react-pro-sidebar";
import {
  Box,
  styled,
  Typography as TypoDefault,
  useTheme,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import {
  ChargerIcon,
  DashboardIcon,
  IdlingIcon,
  LogoutIcon,
  ReportIcon,
  RestrictedIcon,
  TransactionsIcon,
  ReservationIcon,
  LocationIcon,
} from "../../assets/icons/sidebar";
import InfoIcon from "@mui/icons-material/Info";
import CategoryIcon from "@mui/icons-material/Category";
import { Logo, LogoDark } from "../../assets/icons";
import { ROUTES } from "../../routers";
import { Scrollbars } from "react-custom-scrollbars-2";
import { IRole, usePermission } from "../../hooks/CommonContext";
import { EAction } from "../../types/enum";
import LocalGasStationOutlinedIcon from "@mui/icons-material/LocalGasStationOutlined";
import { IconApp } from "../../components";
import QueuingIcon from "../../assets/icons/sidebar/Queuing";
import SubscriptionPlanIcon from "../../assets/icons/sidebar/SubscriptionPlan";
import SubscriptionIcon from "../../assets/icon/Subscription";
import RewardsIcon from "../../assets/icons/sidebar/rewards";
import { AdminPanelSettings, ApartmentOutlined } from "@mui/icons-material";

const REWARDS_TABS = [
  { label: "Types", path: ROUTES.REWARDS_TYPES },
  { label: "Programs", path: ROUTES.REWARDS_PROGRAMS },
  { label: "Codes", path: ROUTES.REWARDS_CODES },
  //{ label: "Points", path: ROUTES.REWARDS },
  //{ label: "Others", path: ROUTES.OTHERS },
  { label: "Brand & Partners", path: ROUTES.BRAND_PARTNERS },
  { label: "Reward Categories", path: ROUTES.REWARDS_CATEGORIES },
  { label: "Banners", path: ROUTES.REWARD_BANNERS },
];

export const ArrMenuSideBar: Array<{
  title: string;
  to: string;
  icon: ReactNode;
  role: string;
  ignoreRoleCpoSp?: IRole[];
  /** When true, isActive only matches the exact path (no startsWith child matching) */
  exact?: boolean;
}> = [
  {
    title: "Dashboard",
    to: ROUTES.DASHBOARD,
    icon: <DashboardIcon />,
    role: "dashboard",
  },
  // NDT-1814: temporarily hidden from the menu; the route is still reachable.
  // {
  //   title: "Dashboard New",
  //   to: ROUTES.DASHBOARD_NEW,
  //   icon: <DashboardIcon />,
  //   role: "dashboard",
  // },
  {
    title: "Admin",
    to: ROUTES.ADMIN,
    icon: <AdminPanelSettings />,
    role: "admin",
  },
  {
    title: "Charger point (EVSE)",
    to: ROUTES.CHARGER_POINT,
    icon: <LocalGasStationOutlinedIcon />,
    role: "chargePoint",
  },
  // NDT-1814: temporarily hidden from the menu; the route is still reachable.
  // {
  //   title: "Charger point (New)",
  //   to: ROUTES.CHARGER_POINT_NEW,
  //   icon: <LocalGasStationOutlinedIcon />,
  //   role: "chargePoint",
  //   ignoreRoleCpoSp: ["CPO", "Seeding Partner"],
  // },
  {
    title: "EVSE",
    to: ROUTES.EVSE,
    icon: <LocalGasStationOutlinedIcon />,
    role: "chargePoint",
    ignoreRoleCpoSp: ["CPO", "Seeding Partner"],
  },
  {
    title: "Charging session",
    to: ROUTES.CHARGE_SESSION,
    icon: <ChargerIcon />,
    role: "chargeSession",
  },
  {
    title: "Transactions",
    to: ROUTES.TRANSACTION,
    icon: <TransactionsIcon />,
    role: "transaction",
  },
  {
    title: "Location",
    to: ROUTES.LOCATION,
    icon: <LocationIcon />,
    role: "location",
  },
  {
    title: "Company",
    to: ROUTES.COMPANY,
    icon: <ApartmentOutlined />,
    role: "company",
    ignoreRoleCpoSp: ["CPO", "Seeding Partner"],
  },
  {
    title: "Idling penalty",
    to: ROUTES.IDLING_ACTIVITIES,
    icon: <IdlingIcon />,
    role: "chargeSession",
  },
  {
    title: "Information",
    to: ROUTES.INFORMATION,
    icon: <InfoIcon />,
    role: "information",
  },
  {
    title: "Information Category",
    to: ROUTES.INFORMATION_CATEGORY,
    icon: <CategoryIcon />,
    role: "informationCategory",
  },
  {
    title: "Reservation",
    to: ROUTES.RESERVATION,
    icon: <ReservationIcon />,
    role: "chargeSession",
  },
  {
    title: "Queuing",
    to: "/queuing",
    icon: <QueuingIcon />,
    role: "transaction",
    ignoreRoleCpoSp: ["CPO", "Seeding Partner"],
  },
  {
    title: "User",
    to: "/user",
    icon: <RestrictedIcon />,
    role: "user",
    exact: true,
  },
  {
    title: "Restricted User",
    to: "/user/restricted-user",
    icon: <RestrictedIcon />,
    role: "restrictedUser",
  },
  {
    title: "Promo Code",
    to: "/promo-codes",
    icon: <ChargerIcon />,
    role: "promoCode",
  },
  {
    title: "Promo Program",
    to: "/promo-programs",
    icon: <ChargerIcon />,
    role: "promoprogram",
  },
  {
    title: "Rewards",
    to: ROUTES.REWARDS,
    icon: <RewardsIcon />,
    role: "rewards",
  },
  {
    title: "Subscription Plans",
    to: "/subscription-plans",
    icon: <SubscriptionPlanIcon />,
    role: "subscriptionplan",
  },
  {
    title: "Subscriptions",
    to: "/subscriptions",
    icon: <SubscriptionIcon />,
    role: "subscription",
  },
  {
    title: "Performance Reports",
    to: ROUTES.REPORTS,
    icon: <ReportIcon />,
    role: "report",
  },
  {
    title: "Audit Log",
    to: ROUTES.AUDIT_LOG,
    icon: <ReportIcon />,
    role: "auditLog",
  },
];

export const Item = memo(
  ({
    title,
    to,
    icon,
    location,
    isCollapsed,
    roleName,
    exact,
  }: {
    title: string;
    to: string;
    icon: React.ReactNode;
    location: string;
    isCollapsed?: boolean;
    roleName?: string;
    /** When true, only an exact path match triggers the active state */
    exact?: boolean;
  }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isActive =
      location === to ||
      (!exact && to !== "/" && location.startsWith(to + "/"));
    const role = useCheck(roleName);

    const canRead = ((role as Array<string>) || []).includes(EAction.READ);
    if (!canRead) return null;

    return (
      <MenuItem
        style={{
          backgroundColor: isActive && isCollapsed ? "#c7c7c7" : "unset",
          display: "flex",
          marginLeft: isActive ? "-10px" : 0,
        }}
        component={<Link to={to} />}
        icon={isActive ? icon : undefined}
      >
        <Typography
          color={isActive ? colors.primary["activeText"] : "#c7c7c7"}
          fontSize={"16px"}
          fontWeight={isActive ? "700" : "300"}
        >
          {title}
        </Typography>
      </MenuItem>
    );
  },
);

// const useStyles = makeStyles<Record<string, any>>({
//   btnToggleDarkMode: {
//     outline: "none",
//     border: "none",
//     background: "inherit",
//     cursor: "pointer",
//     display: "flex",
//     alignItem: "center",
//     // position: "absolute",
//     // top: "10px",
//     // right: "23px",
//   },
// });

const SidebarLayout = () => {
  const [isCollapsed] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  // const colorMode = useContext(ColorModeContext);
  // const classes = useStyles() as any;
  const navigate = useNavigate();
  const { profile } = usePermission();
  const queryClient = useQueryClient();
  const [openRewards, setOpenRewards] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>(null);

  useEffect(() => {
    const foundTab = REWARDS_TABS.find(
      (tab) =>
        location.pathname === tab.path ||
        location.pathname.startsWith(`${tab.path}/`)
    );
    setActiveTab(foundTab ? foundTab.path : null);
    const shouldOpen = REWARDS_TABS.some(
      (tab) =>
        location.pathname === tab.path ||
        location.pathname.startsWith(`${tab.path}/`)
    );
    setOpenRewards(shouldOpen);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("profile");
    queryClient.clear();
    navigate(ROUTES.LOGIN);
  };
  const roleRewards = useCheck("rewards");
  const canReadRewards = ((roleRewards as Array<string>) || []).includes(EAction.READ);

  return (
    <Box
      position={"relative"}
      sx={{
        borderRadius: {
          xs: "0px",
          md: "20px",
        },
      }}
      overflow={"hidden"}
      width="270px"
    >
      <Sidebar
        rootStyles={{
          height: "100%",
          paddingRight: "0px",
          [`.${sidebarClasses.container}`]: {
            backgroundColor: colors.primary["bg"],
            // padding: "50px 20px 15px",
            display: !isCollapsed ? "flex" : "block",
            width: "270px",
          },
        }}
        collapsed={isCollapsed}
      >
        <Menu
          rootStyles={{
            height: "100%",
            width: "100%",
            [`ul`]: {
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: "15px",
              height: "100%",
            },
          }}
          menuItemStyles={{
            button: {
              paddingLeft: "40px",
              borderRadius: "5px",
            },
          }}
        >
          <Box>
            <Box
              display={"flex"}
              justifyContent="space-between"
              width={"100%"}
              p="50px 20px 30px 40px"
              alignItems="center"
            >
              <img
                onClick={() => navigate(ROUTES.DASHBOARD)}
                src={theme.palette.mode === "light" ? Logo : LogoDark}
                alt="Logo"
                style={{
                  cursor: "pointer",
                }}
              />
            </Box>
            <Box display={"flex"} gap={2} p="40px" alignItems={"start"}>
              <IconApp name={"User"} width={35} height={35} />
              {/* <img */}
              {/*   src={AvtImg} */}
              {/*   style={{ */}
              {/*     minWidth: "40px", */}
              {/*     maxWidth: "40px", */}
              {/*     height: "40px", */}
              {/*     borderRadius: "6px", */}
              {/*     overflow: "hidden", */}
              {/*   }} */}
              {/* /> */}
              <Box display="flex" flexDirection={"column"} overflow="hidden">
                <Typography fontSize={"15px"} fontWeight={"700"}>
                  {profile?.name}
                </Typography>
                <Typography fontSize={"11px"} color="#c7c7c7">
                  {profile?.email}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box flex={1}>
            <Scrollbars style={{ height: "100%" }} autoHide>
              {ArrMenuSideBar.map((item, index) => {
                if (item.title === "Rewards") {
                  if (!canReadRewards) {
                    return null;
                  }
                  const rewardColor = openRewards ? "#FFD600" : "#c7c7c7";
                  return (
                    <Box
                      key="rewards-submenu"
                      sx={{
                        background: colors.primary.bg,
                        borderRadius: "8px",
                        overflow: "hidden",
                        userSelect: "none",
                      }}
                    >
                      <Box
                        onClick={() => setOpenRewards((p) => !p)}
                        aria-expanded={openRewards}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 2,
                          height: 40,
                          cursor: "pointer",
                          "&:hover": { background: "#1c1c1c" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            ml: 2,
                            transition: "margin-left 0.25s ease",
                          }}
                        >
                          <Box
                            sx={{
                              width: openRewards ? 20 : 0,
                              height: 20,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              overflow: "hidden",
                              transition:
                                "width 0.25s ease, opacity 0.25s ease",
                              opacity: openRewards ? 1 : 0,
                            }}
                          >
                            <RewardsIcon color={rewardColor} />
                          </Box>

                          <Typography
                            fontSize="16px"
                            fontWeight={openRewards ? 700 : 400}
                            sx={{
                              color: rewardColor,
                              transition:
                                "color 0.25s ease, font-weight 0.2s ease",
                              letterSpacing: 0.2,
                            }}
                          >
                            Rewards
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          overflow: "hidden",
                          maxHeight: openRewards
                            ? `${REWARDS_TABS.length * 45}px`
                            : "0px",
                          opacity: openRewards ? 1 : 0,
                          transition:
                            "max-height 0.35s ease, opacity 0.35s ease",
                        }}
                      >
                        {REWARDS_TABS.map((tab, i) => {
                          const isActive = activeTab === tab.path;
                          return (
                            <Box
                              key={tab.path}
                              onClick={() => {
                                if (!isActive) {
                                  setActiveTab(tab.path);
                                  navigate(tab.path);
                                }
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                minHeight: 38,
                                mx: 1,
                                mt: i === 0 ? 1 : 0.5,
                                cursor: isActive ? "default" : "pointer",
                                opacity: isActive ? 1 : 0.8,
                                transition: "all 0.25s ease",
                                transform: openRewards
                                  ? `translateY(0px)`
                                  : `translateY(-5px)`,
                                transitionDelay: openRewards
                                  ? `${i * 50}ms`
                                  : "0ms",
                                pl: 7,
                              }}
                            >
                              <Typography
                                fontSize="16px"
                                fontWeight={isActive ? 600 : 350}
                                sx={{
                                  color: isActive ? "#FFD600" : "#c7c7c7",
                                  textAlign: "left",
                                  letterSpacing: 0.2,
                                  transition: "color 0.2s ease",
                                }}
                              >
                                {tab.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                }

                if (profile?.role === "Superadmin") {
                  return (
                    <Item
                      key={index}
                      title={item.title}
                      to={item.to}
                      icon={item.icon}
                      location={location.pathname}
                      isCollapsed={isCollapsed}
                      roleName={item.role}
                      exact={item.exact}
                    />
                  );
                } else if (!item.ignoreRoleCpoSp?.includes(profile?.role!)) {
                  return (
                    <Item
                      key={index}
                      title={item.title}
                      to={item.to}
                      icon={item.icon}
                      location={location.pathname}
                      isCollapsed={isCollapsed}
                      roleName={item.role}
                      exact={item.exact}
                    />
                  );
                }
                return null;
              })}
            </Scrollbars>
          </Box>
          <Box
            onClick={handleLogout}
            // paddingLeft={!isCollapsed ? "15px" : "0px"}
            p={"0px 0px 40px 40px"}
            // height={"48px"}
            display={"flex"}
            alignItems={"center"}
            gap={"20px"}
            style={{ cursor: "pointer" }}
          >
            <LogoutIcon color={colors.primary.activeText} />
            {!isCollapsed && (
              <Typography
                color={"#c7c7c7"}
                fontSize={"16px"}
                // fontWeight={"700"}
              >
                Log out
              </Typography>
            )}
          </Box>
        </Menu>
        <Box
          position={"absolute"}
          bottom={10}
          left={isCollapsed ? "50%" : "20%"}
        >
          <Typography fontSize={"12px"} color={"#c7c7c7"}>
            Version: {process.env.REACT_APP_BUILD_VERSION || "N/A"}
          </Typography>
        </Box>
      </Sidebar>
    </Box>
  );
};

export default memo(SidebarLayout);

const useCheck = (role?: string) => {
  const roleDefault = usePermission();

  const permission = useMemo(() => {
    return (roleDefault as any)[role || ""];
  }, [role, roleDefault]);

  return permission;
};

const Typography = styled(TypoDefault)({
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});
